import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { WEB_COMPONENT_NETWORK_MALWARE_SCAN } from '../../../../../webcomponents.constants';
import { customElement, property, query, state } from 'lit/decorators.js';
import { translateFn } from '../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../../../ui/gzDropdown';
import { GzInputWebComponent } from '../../../../../ui/gzInput/component/GzInputWebComponent';
import { GzCheckboxWebComponent } from '../../../../../ui/gzCheckbox';
import { SelectOption } from '../../../../../ui/gzDropdown/types';
import { styles } from './view/styles';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../ui/gzInput';
import {
  CustomExclusionColumn,
  CustomExclusionItem,
  MALWARE_SCAN_MISCELLANEOUS,
  MalwareScanForm,
  ScanTargetColumn,
  ScanTargetFilesItem,
} from './types';
import { ButtonColor, ButtonFill, ButtonSize, ButtonType } from '../../../../../ui/gzIconButton/types';
import { MalwareScanHelper } from './helpers/malware.scan.helper';
import { FormGroup } from '../../../../../ui/gzForm/formGroup/form-group';
import { GzRadioGroupWebComponent } from '../../../../../ui/gzRadio';
import { GzIconButtonWebComponent } from '../../../../../ui/gzIconButton';
import { elementClassListAdd, elementClassListRemove, isHidden } from '../../../../../utils/htmlElement.utils';
import { InfoBoxSeverity } from '../../../../../ui/gzInfoBox/types';
import { FormGroupChild } from '../../../../../ui/gzForm/formGroup/types/form-group-children';
import { Subscription } from 'rxjs';
import { GzDataTableWebComponent } from '../../../../../ui/gzDataTable';
import { CustomizableValidator } from '../../../../../ui/gzForm/validators/customizable.validator';
import { ValidationResult } from '../../../../../ui/gzForm/formField/types/form-validator';
import { isEqual } from 'lodash';
import { RequiredValidator } from '../../../../../ui/gzForm/validators/required.validator';
import { NetworkPopUpEvents } from '../../NetworkBasePopUp';
import { GzChipsGeneratorInputWebComponent } from '../../../../../ui/gzChipsGeneratorInput';
import { FontSizes, FontWeights } from 'apps/webcomponents/src/types/common/types';
import { GzTextWebComponent } from '../../../../../ui/gzText';

@customElement(WEB_COMPONENT_NETWORK_MALWARE_SCAN)
export class GzMalwareScanWebComponent extends LitElement {
  @query('gz-form-field#scan-type')
  typeFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;

  @query('gz-form-field#scan-name')
  nameFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('gz-form-field#scan-option')
  scanOptionFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;

  @query('gz-form-field#low-priority')
  lowPriorityFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#shut-down-after')
  shutDownFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#pause-when-on-battery')
  pauseWhenOnBatteryField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#cpu-usage-control-enabled')
  cpuEnableUsageField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#cpu-usage-control-maximum-usage')
  maximumUsageField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;

  @query('gz-form-field#scan-file-types')
  fileTypeFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;

  @query('div#custom-defined-extensions-container')
  customDefinedExtensionsContainer: HTMLElement;

  @query('gz-chips-generator-input#custom-extensions-chips-generator')
  customExtensionsChipsGenerator: GzChipsGeneratorInputWebComponent;

  @query('gz-form-field#scan-inside-archives')
  scanInsideArchiveFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#archive-limit-size')
  archiveScanLimitFormField: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;

  @query('gz-form-field#archive-max-depth')
  archiveMaxDepthFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;

  @query('gz-form-field#scan-email-archives')
  scanEmailArchiveFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  // Miscellaneous START_REGION
  @query('gz-form-field#scan-boot-sectors')
  scanBootFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#scan-uefi')
  scanUefiFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#scan-registry')
  scanRegistryFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#scan-rootkits')
  scanRootKitsFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#scan-keylogger')
  scanKeyloggerFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#scan-network-shares')
  scanNetworkSharesFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#scan-memory')
  scanMemoryFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#scan-cookies')
  scanCookiesFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#scan-new-and-changed-files')
  scanNewAndChangedFilesFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#scan-pua')
  scanPuaFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#scan-after-product-update')
  scanAfterProductUpdateFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#scan-volumes')
  scanVolumesFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#preserve-last-access')
  preserveLastAccessFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  // Miscellaneous END_REGION

  @query('div#network-credentials-form')
  networkCredentialsForm: HTMLElement;

  @query('gz-form-field#network-type-username')
  usernameFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('gz-form-field#network-type-password')
  passwordFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('gz-form-field#default-infected-action')
  defaultInfectedFilesActionFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;

  @query('gz-form-field#rootkits-action')
  rootkitsActionFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;

  @query('gz-form-field#default-pua-action')
  defaultPuaActionFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;

  @query('gz-form-field#scan-target-type')
  scanTargetTypeFormField: GzFormFieldWebComponent<GzInputWebComponent, string | number>;

  @query('gz-input#scan-target-input')
  scanTargetInput: GzInputWebComponent;

  @query('gz-form-field#exclusions-menu')
  exclusionGeneralTypeFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;

  @query('gz-form-field#exclusion-type')
  exclusionTypeFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;

  @query('gz-form-field#exclusion-path')
  exclusionPathFormField: GzFormFieldWebComponent<GzInputWebComponent, string | number>;

  @query('gz-input#exclusion-path-input')
  exclusionPathInput: GzInputWebComponent;

  @query('gz-icon-button#add-custom-exclusion')
  addCustomExclusionIconButton: GzIconButtonWebComponent;

  @query('#general-form')
  generalForm: HTMLElement;

  @query('#options-form')
  optionsForm: HTMLElement;

  @query('#settings')
  settings: HTMLElement;

  @query('#target')
  target: HTMLElement;

  @query('#exclusion-form-input')
  customExclusionsContainer: HTMLElement;

  @query('#add-target-icon-button')
  addTargetIconButton: GzIconButtonWebComponent;

  @query('gz-form-field#form-scan-target-table')
  scanTargetTableFormField: GzFormFieldWebComponent<GzDataTableWebComponent<ScanTargetFilesItem>>;

  @query('#scan-target-table')
  scanTargetTable: GzDataTableWebComponent<ScanTargetFilesItem>;

  @query('#custom-exclusion-table')
  customExclusionTable: GzDataTableWebComponent<CustomExclusionItem>;

  @query('#exclusions-text')
  exclusionsTextElement: GzTextWebComponent;

  @query('#scan-emails-text')
  scanEmailsTextElement: GzTextWebComponent;

  @query('#scan-archives-text')
  scanArchivesTextElement: GzTextWebComponent;

  @property({ attribute: true, reflect: true, type: Number })
  type: number = Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_QUICK;

  @property({ attribute: true, reflect: true, type: Boolean })
  shouldShowNetworkType = false;

  @state()
  private shouldShowWarningInfo = false;

  @state()
  private shouldShowMaximumUsageDropdown = false;

  @state()
  private customExclusionType: number | null;

  private checkedMiscellaneous: Array<MALWARE_SCAN_MISCELLANEOUS> = MalwareScanHelper.getMiscellaneousFromType(
    this.type
  );

  private exclusionItems: CustomExclusionItem[] = [];

  private scanFilesItems: ScanTargetFilesItem[] = MalwareScanHelper.getDefaultScanTargetPathFromType(this.type);

  private generalFormGroup: FormGroup;

  private formGroup: FormGroup;

  private miscellaneousFormGroup: FormGroup;

  private archivesFormGroup: FormGroup;

  private actionFormGroup: FormGroup;

  private propsChangedFromInside = false;

  private generalValidationResult = true;

  private settingsValidationResult = true;

  /**
   * List of subscriptions to unsubscribe from when the component is destroyed.
   * @private
   */
  private subscriptions: Subscription[] = [];

  static get styles() {
    return styles;
  }

  protected async updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.buildFormGroup();
    this.setFormValidators();
    this.subscribeToSettingsFormFields();
    this.setTranslatedStrings();
  }

  /**
   * We can not use the translateFn function with parameters directly in the render functions.
   * In order to have the surrounding HTML tags correctly rendered, we need to set this strings in this matter.
   * @private
   */
  private setTranslatedStrings(): void {
    const exclusionsText = translateFn(
      'Use the exclusions defined in {OPEN_TAG} Policy > Antimalware > Settings {CLOSE_TAG}',
      {
        OPEN_TAG: '<b>',
        CLOSE_TAG: '</b>',
      }
    );

    const kbLink = Application.Main.company.isPartner()
      ? 'https://www.bitdefender.com/business/support/en/77211-80154-types-of-archives-scanned-by-bitdefender-endpoint-security-tools.html'
      : 'https://www.bitdefender.com/business/support/en/77209-80154-types-of-archives-scanned-by-bitdefender-endpoint-security-tools.html';

    const scanEmailsString = translateFn(
      'Enable scanning of email message files and email databases, including file formats such as EML, MSG, PST, DBX, MBX, and TBB. Email archive scanning is resource intensive and can affect system performance. For the list of scanned archive types, refer to {OPEN_TAG} this article. {CLOSE_TAG}',
      {
        OPEN_TAG: `<gz-link href="${kbLink}">`,
        CLOSE_TAG: '</gz-link>',
      }
    );

    const scanArchivesText = translateFn(
      'Enable scanning in archives that are within the specified size and depth limits. Bitdefender engines can scan many types of archives that may contain threats. For the list of scanned archive types, refer to {OPEN_TAG} this article. {CLOSE_TAG}',
      {
        OPEN_TAG: `<gz-link href="${kbLink}">`,
        CLOSE_TAG: '</gz-link>',
      }
    );

    this.exclusionsTextElement.innerHTML = exclusionsText;
    this.scanEmailsTextElement.innerHTML = scanEmailsString;
    this.scanArchivesTextElement.innerHTML = scanArchivesText;
  }

  public getFormValue(): MalwareScanForm {
    return {
      general: this.generalFormGroup.value,
      settings: this.formGroup.getValueRecursively(this.formGroup, true),
      scanTargetFilesAndFolders: this.getScanTargetTableValues(),
      exclusionItems: MalwareScanHelper.transformCustomExclusionsPayload(this.exclusionItems),
      userCustomExtensions: this.customExtensionsChipsGenerator.getFormValue(),
    };
  }

  private setFormValidators() {
    this.setNameValidators();
    this.setUsernameAndPasswordValidators();
    this.setScanTargetFormFieldValidator();
    this.updateScanTargetValidators();
    this.updateCustomExclusionPathValidators();

    this.generalFormGroup.valid$.subscribe(value => {
      this.generalValidationResult = value;
      const validationResult = this.generalValidationResult && this.settingsValidationResult;
      this.dispatchEvent(
        new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
          detail: { value: !validationResult },
          bubbles: true,
          composed: true,
        })
      );
    });

    this.formGroup.valid$.subscribe(value => {
      this.settingsValidationResult = value;
      const validationResult = this.generalValidationResult && this.settingsValidationResult;
      this.dispatchEvent(
        new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
          detail: { value: !validationResult },
          bubbles: true,
          composed: true,
        })
      );
    });
  }

  private updateFormValidators() {
    this.updateScanTargetValidators();
    this.updateCustomExclusionPathValidators();
  }

  private setNameValidators() {
    if (!this.nameFormField) {
      return;
    }

    this.nameFormField.Validators = [RequiredValidator(translateFn('Required field'))];
  }

  private setUsernameAndPasswordValidators() {
    if (!this.usernameFormField || !this.passwordFormField) {
      return;
    }

    this.usernameFormField.Validators = [
      CustomizableValidator(this.getCredentialsValidator, this.networkCredentialsForm),
    ];
    this.passwordFormField.Validators = [
      CustomizableValidator(this.getCredentialsValidator, this.networkCredentialsForm),
    ];
  }

  private getCredentialsValidator(value: string | undefined, formContainer: HTMLElement): ValidationResult {
    if (isHidden(formContainer)) {
      return {
        valid: true,
      };
    }

    if (value === undefined || value === '' || value.trim() === '') {
      return {
        valid: false,
        error: translateFn('Required field'),
      };
    }

    return {
      valid: true,
    };
  }

  private updateScanTargetValidators() {
    if (!this.scanTargetTypeFormField) {
      return;
    }

    this.scanTargetTypeFormField.Validators = [CustomizableValidator(this.validateEmptyStateForScanTarget.bind(this))];
    this.scanTargetTableFormField.validate();
  }

  private validateEmptyStateForScanTarget(): ValidationResult {
    const tableValues = this.getScanTargetTableValues();
    const formValue = (this.scanTargetTypeFormField.value as string).trim();
    if (tableValues.length === 0 && (!formValue || formValue === '')) {
      return {
        valid: false,
        error: translateFn('Required field'),
      };
    }

    if (tableValues.includes(formValue)) {
      return {
        valid: false,
        error: translateFn('The path already exists.'),
      };
    }

    return {
      valid: true,
    };
  }

  private setScanTargetFormFieldValidator() {
    if (!this.scanTargetTableFormField) {
      return;
    }

    const validatorFunction = (): ValidationResult => {
      if (this.getScanTargetTableValues().length === 0) {
        return {
          valid: false,
          error: 'At least one field is required.',
        };
      }

      return {
        valid: true,
      };
    };

    this.scanTargetTableFormField.Validators = [CustomizableValidator(validatorFunction)];
  }

  private updateCustomExclusionPathValidators() {
    if (!this.exclusionPathFormField) {
      return;
    }

    this.exclusionPathFormField.Validators = [CustomizableValidator(this.validateAddingCustomExclusion.bind(this))];
  }

  private validateAddingCustomExclusion(): ValidationResult {
    const type = this.exclusionTypeFormField.value;
    if (!type || type === '') {
      return {
        valid: false,
        error: translateFn('Select an exclusion type.'),
      };
    }

    const valueToAdd = (this.exclusionPathFormField.value as string).trim();
    const tableValues = this.getCustomExclusionTableValues();
    const valueExistsForSameType = tableValues
      .filter(item => item[CustomExclusionColumn.TYPE] === type)
      .filter(item => item[CustomExclusionColumn.PATH] === valueToAdd);

    if (valueExistsForSameType.length !== 0) {
      return {
        valid: false,
        error:
          type === Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_TYPE.EXTENSION
            ? translateFn('The entry already exists.')
            : translateFn('The path already exists.'),
      };
    }

    return {
      valid: true,
    };
  }

  private getScanTargetTableValues(): Array<string> {
    if (!this.scanTargetTable || !this.scanTargetTable.getFormValue()) {
      return [];
    }

    return (this.scanTargetTable.getFormValue() as ScanTargetFilesItem[]).map(item => item[ScanTargetColumn.PATH]);
  }

  private getCustomExclusionTableValues(): Array<CustomExclusionItem> {
    if (!this.customExclusionTable || !this.customExclusionTable.getFormValue()) {
      return [];
    }

    return this.customExclusionTable.getFormValue() as CustomExclusionItem[];
  }

  private buildFormGroup() {
    this.generalFormGroup = FormGroup.build({
      taskName: this.nameFormField,
      scanType: this.typeFormField,
      shutDownWhenFinished: this.shutDownFormField,
      pauseWhenOnBattery: this.pauseWhenOnBatteryField,
      runTask: this.lowPriorityFormField,
      cpuUsageControl: {
        enabled: this.cpuEnableUsageField,
        maximumUsage: this.maximumUsageField,
      },
    });

    this.miscellaneousFormGroup = FormGroup.build({
      scanBoot: this.scanBootFormField,
      scanUefi: this.scanUefiFormField,
      scanRegistry: this.scanRegistryFormField,
      scanRootkits: this.scanRootKitsFormField,
      ignoreKeyloggers: this.scanKeyloggerFormField,
      scanNetworkFiles: this.scanNetworkSharesFormField,
      scanMemory: this.scanMemoryFormField,
      scanCookie: this.scanCookiesFormField,
      scanNewChanged: this.scanNewAndChangedFilesFormField,
      scanPUA: this.scanPuaFormField,
      resumeIfInterrupted: this.scanAfterProductUpdateFormField,
      scanDetachableVolumes: this.scanVolumesFormField,
      preserveLastAccessTime: this.preserveLastAccessFormField,
    });

    this.archivesFormGroup = FormGroup.build({
      enable: this.scanInsideArchiveFormField,
      scanEmail: this.scanEmailArchiveFormField,
      settings: {
        limitArchiveSize: this.archiveScanLimitFormField,
        maximumArchiveDepth: this.archiveMaxDepthFormField,
      },
    });

    this.actionFormGroup = FormGroup.build({
      whenInfected: {
        action: this.defaultInfectedFilesActionFormField,
      },
      whenRootKitAction: {
        action: this.rootkitsActionFormField,
      },
      whenPua: {
        action: this.defaultPuaActionFormField,
      }
    });

    this.formGroup = FormGroup.build({
      scanOption: this.scanOptionFormField,
      fileTypes: this.fileTypeFormField,
      archives: this.archivesFormGroup,
      miscellaneous: this.miscellaneousFormGroup,
      action: this.actionFormGroup,
      exclusionProfile: this.exclusionGeneralTypeFormField,
      credentials: {
        user: this.usernameFormField,
        password: this.passwordFormField,
      },
      scanTargets: this.scanTargetTableFormField,
    });

    this.disableFormIfNecessary();
  }

  private shouldShowExtraCheckboxes() {
    const acceptedTypes = [
      Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_MEMORY,
      Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_CUSTOM,
      Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK,
    ];
    return acceptedTypes.includes(this.type);
  }

  private subscribeToFormField(formField: GzFormFieldWebComponent): void {
    const subscription: Subscription = formField.value$.subscribe(() => {
      if (this.propsChangedFromInside) {
        return;
      }

      this.scanOptionFormField.Input.selectOption(Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_CUSTOM);
    });
    this.subscriptions.push(subscription);
  }

  private subscribeToFormFieldsAndUpdateScanOption(formGroup: FormGroup): void {
    formGroup.applyToFormGroupFields((formField: FormGroupChild) => {
      if (formField instanceof GzFormFieldWebComponent) {
        this.subscribeToFormField(formField);
      }
    });
  }

  private subscribeToDisableAddTargetIconButton() {
    const validationSubscription: Subscription = this.scanTargetTypeFormField.valid$.subscribe(value => {
      const currentFormValue = this.scanTargetTypeFormField.value;
      if (!currentFormValue || currentFormValue.toString().trim() === '') {
        this.addTargetIconButton.disabled = true;
        return;
      }

      this.addTargetIconButton.disabled = !value;
    });

    this.subscriptions.push(validationSubscription);
  }

  private subscribeToDisableAddCustomExclusion() {
    const validationSubscription: Subscription = this.exclusionPathFormField.valid$.subscribe(value => {
      const currentFormValue = (this.exclusionPathFormField.value as string).trim();
      if (!currentFormValue || currentFormValue === '') {
        this.addCustomExclusionIconButton.disabled = true;
        return;
      }

      this.addCustomExclusionIconButton.disabled = !value;
    });

    this.subscriptions.push(validationSubscription);
  }

  private subscribeToSettingsFormFields(): void {
    const settingsFormFields = [this.archivesFormGroup, this.miscellaneousFormGroup, this.actionFormGroup];

    settingsFormFields.forEach((formField): void => {
      if (formField instanceof FormGroup) {
        this.subscribeToFormFieldsAndUpdateScanOption(formField);
      }
    });

    this.subscribeToDisableAddTargetIconButton();
    this.subscribeToDisableAddCustomExclusion();
    this.subscribeToFormField(this.fileTypeFormField);
  }

  public unsubscribeFromObservables(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    this.subscriptions = [];
  }

  disconnectedCallback() {
    this.unsubscribeFromObservables();
    super.disconnectedCallback();
  }

  render() {
    return html`
      <div class="container">
        <div class="form-section mb-3 general-form" id="general-form">
          <gz-title
            class="form-subtitle title-container text-black"
            fontSize="${FontSizes.HEADING_4}"
            fontWeight="${FontWeights.MEDIUM}"
            >${translateFn('General')}</gz-title
          >
          <gz-title
            fontSize="${FontSizes.PARAGRAPH_11}"
            fontWeight="${FontWeights.SEMIBOLD}"
            class="text-uppercase field-container form-subtitle"
            >${translateFn('Details')}</gz-title
          >
          <gz-form-field id="scan-type" label="${translateFn('Type')}">
            <gz-dropdown
              gz-form-input
              @selectionChanged="${this.handleTypeChanged}"
              options="${JSON.stringify(MalwareScanHelper.getScanTypeOptions(this.shouldShowNetworkType))}"
              .value="${this.type}"
              placeholder="${translateFn('Select scan type')}"
            ></gz-dropdown>
          </gz-form-field>
          <gz-form-field id="scan-name" label="${translateFn('Task name') + '*'}" class="mb-2">
            <gz-input
              gz-form-input
              inputWidth="${InputWidthValues.LARGE}"
              value="${MalwareScanHelper.getScanTitleFromType(this.type)}"
            ></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>
          <gz-form-field id="low-priority" class="${this.shouldShowExtraCheckboxes() ? '' : 'hidden'} field-container">
            <gz-checkbox gz-form-input id="low-priority" label="${translateFn('Run the task with low priority')}">
            </gz-checkbox>
          </gz-form-field>
          <gz-form-field
            id="shut-down-after"
            class="${this.shouldShowExtraCheckboxes() ? '' : 'hidden'} field-container"
          >
            <gz-checkbox
              gz-form-input
              @change="${this.handleShutDownToggled}"
              id="shut-down-after"
              label="${translateFn('Shut down computer when scan is finished')}"
            >
            </gz-checkbox>
          </gz-form-field>
          <gz-form-field
            id="pause-when-on-battery"
            class="${this.shouldShowExtraCheckboxes() ? '' : 'hidden'} field-container"
          >
            <gz-checkbox
              gz-form-input
              id="pause-when-on-battery"
              label="${translateFn('Pause scan when computer is in Battery mode')}"
            >
            </gz-checkbox>
          </gz-form-field>
          <div class="form-section">
            <gz-form-field
              id="cpu-usage-control-enabled"
              class="${this.shouldShowExtraCheckboxes() ? '' : 'hidden'} field-container flex-group"
            >
              <div class="horizontal-layout-usage-control">
                <div class="checkbox-container-usage-control">
                  <gz-checkbox
                    gz-form-input
                    @change="${this.handleEnableCpuUsageToggled}"
                    id="cpu-usage-control-enabled"
                    label="${translateFn('Enable CPU usage control')}"
                  >
                  </gz-checkbox>
                </div>
              </div>
            </gz-form-field>
          </div>

          <gz-form-field
            id="cpu-usage-control-maximum-usage"
            class="${this.shouldShowExtraCheckboxes() && this.shouldShowMaximumUsageDropdown ? '' : 'hidden'}"
            label="${translateFn('Set maximum CPU usage')}"
          >
            <gz-dropdown
              gz-form-input
              id="cpu-usage-control-maximum-usage"
              options="${JSON.stringify(MalwareScanHelper.getMaximumUsageOptions())}"
              .value="${MalwareScanHelper.getDefaultValueMaximumCpuUsageControl()}"
            ></gz-dropdown>
          </gz-form-field>

          <gz-info-box
            severity="${InfoBoxSeverity.WARNING}"
            class="${this.shouldShowExtraCheckboxes() && this.shouldShowWarningInfo ? '' : 'hidden'}"
          >
            ${translateFn(
              'Endpoints will shut down immediately after the scan is finished. To avoid disrupting user activity, keep this option disabled.'
            )}
          </gz-info-box>
        </div>
        ${this.renderScanOptions()}
        <div class="form-section mb-2 mt-1 ${this.isFormDisabled() ? ' disabled' : ''}" id="settings">
          <gz-title
            fontSize="${FontSizes.PARAGRAPH_11}"
            fontWeight="${FontWeights.SEMIBOLD}"
            class="text-uppercase field-container form-subtitle"
            >${translateFn('Settings')}</gz-title
          >
          <div class="pb-1">
            <gz-form-field id="scan-file-types" label="${translateFn('File types')}">
              <gz-dropdown
                gz-form-input
                @selectionChanged="${this.handleFileTypeSelectionChanged}"
                options="${JSON.stringify(MalwareScanHelper.getFileTypeOptions())}"
                .value="${MalwareScanHelper.getDefaultFileType()}"
              ></gz-dropdown>
            </gz-form-field>
            ${this.renderUserDefinedExtensions()} ${this.renderArchives()} ${this.renderMiscellaneous()}
            ${this.renderActions()}
          </div>
        </div>
        <div class="${'form-section' + this.isFormDisabled() ? ' disabled' : ''}" id="target">
          <gz-title
            class="form-subtitle title-container text-black"
            fontSize="${FontSizes.HEADING_4}"
            fontWeight="${FontWeights.MEDIUM}"
            >${translateFn('Target')}</gz-title
          >
          ${this.renderTargetSection()} ${this.renderNetworkCredentials()} ${this.renderExclusions()}
        </div>
      </div>
    `;
  }

  private renderScanOptions(): TemplateResult {
    return html`
      <div class="${'form-section' + this.isFormDisabled() ? ' disabled' : ''}" id="options-form">
        <gz-title
          class="form-subtitle title-container text-black"
          fontSize="${FontSizes.HEADING_4}"
          fontWeight="${FontWeights.MEDIUM}"
          >${translateFn('Options')}</gz-title
        >
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="text-uppercase field-container form-subtitle"
          >${translateFn('Scan Options')}</gz-title
        >
        <gz-form-field @radioSelectionChanged="${this.handleScanOptionChanged}" id="scan-option">
          <gz-radio-group
            gz-form-input
            groupName="scan-type"
            class="scan-options-group"
            .selectedOption="${MalwareScanHelper.getDefaultOptionFromType(this.type)}"
          >
            <gz-radio
              name="scan-type"
              .value="${Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_AGRESSIVE}"
              label="${translateFn('Aggressive')}"
              class="field-container"
            ></gz-radio>
            <div class="extra-scan-info mb-2">
              <gz-text fontSize="${FontSizes.PARAGRAPH_12}" lineHeight="1" class="helper-text mb-1">
                ${translateFn('Advanced security, moderate use of resources.')}
              </gz-text>
              <ul class="list mb-2">
                <li><gz-text fontSize="${FontSizes.PARAGRAPH_12}"> ${translateFn('Scan all files')} </gz-text></li>

                <li>
                  <gz-text fontSize="${FontSizes.PARAGRAPH_12}"> ${translateFn('Scan archives and emails')} </gz-text>
                </li>

                <li>
                  <gz-text fontSize="${FontSizes.PARAGRAPH_12}">
                    ${translateFn('Scan memory, boot sectors, registry')}
                  </gz-text>
                </li>
              </ul>
            </div>
            <gz-radio
              name="scan-type"
              class="field-container"
              .value="${Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_NORMAL}"
              label="${translateFn('Normal')}"
            >
            </gz-radio>
            <div class="extra-scan-info mb-2">
              <gz-text lineHeight="1" fontSize="${FontSizes.PARAGRAPH_12}" class="helper-text mb-1">
                ${translateFn('Standard security, low use of resources.')}
              </gz-text>

              <ul class="list mb-2">
                <li>
                  <gz-text fontSize="${FontSizes.PARAGRAPH_12}">
                    ${translateFn('Scan all files that are new or were changed')}
                  </gz-text>
                </li>

                <li>
                  <gz-text fontSize="${FontSizes.PARAGRAPH_12}"> ${translateFn('Scan archives and emails')} </gz-text>
                </li>
              </ul>
            </div>

            <gz-radio
              name="scan-type"
              class="field-container"
              .value="${Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_PERMISSIVE}"
              label="${translateFn('Permissive')}"
            >
            </gz-radio>
            <div class="extra-scan-info mb-2">
              <gz-text lineHeight="1" fontSize="${FontSizes.PARAGRAPH_12}" class="helper-text mb-1"
                >${translateFn('Basic security, very low use of resources.')}</gz-text
              >

              <ul class="list mb-2">
                <li>
                  <gz-text fontSize="${FontSizes.PARAGRAPH_12}">
                    ${translateFn('Scan only application files that are new or were changed')}
                  </gz-text>
                </li>

                <li>
                  <gz-text fontSize="${FontSizes.PARAGRAPH_12}"> ${translateFn('Scan emails')} </gz-text>
                </li>
              </ul>
            </div>

            <gz-radio
              name="scan-type"
              class="field-container"
              .value="${Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_CUSTOM}"
              label="${translateFn('Custom')}"
            ></gz-radio>
            <gz-text lineHeight="1" fontSize="${FontSizes.PARAGRAPH_12}" class="helper-text mb-1"
              >${translateFn('Administrator-defined settings.')}</gz-text
            >
          </gz-radio-group>
        </gz-form-field>
      </div>
    `;
  }

  private renderUserDefinedExtensions(): TemplateResult {
    return html`
      <div id="custom-defined-extensions-container" class="hidden position-relative">
        <gz-chips-generator-input
          id="custom-extensions-chips-generator"
          inputLines="3"
          .strings="${MalwareScanHelper.getStringsForUserDefinedExtensions()}"
          hideCopyToClipboardButton
        >
        </gz-chips-generator-input>
        <gz-link
          class="link mb-2 suggestions-link"
          href="${Application.Main.company.isPartner()
            ? 'https://www.bitdefender.com/business/support/en/77211-95338-supported-file-types.html'
            : 'https://www.bitdefender.com/business/support/en/77209-95338-supported-file-types.html'}"
        >
          ${translateFn('View list of suggestions')}
        </gz-link>
        <gz-text fontSize="${FontSizes.PARAGRAPH_12}" class="mb-1 mt-1">
          ${translateFn('User-defined extensions are applicable only for Windows operating systems.')}
        </gz-text>
      </div>
    `;
  }

  private renderArchives(): TemplateResult {
    return html`
      <div class="pl-2 mt-2">
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="text-uppercase field-container form-subtitle"
          >${translateFn('Archives')}</gz-title
        >
        <gz-form-field id="scan-inside-archives" class="field-container">
          <gz-checkbox
            @change="${this.handleScanInsideArchiveToggle}"
            gz-form-input
            .checked="${MalwareScanHelper.getScanInsideArchiveFromType(this.type)}"
            id="scan-inside-archives"
            label="${translateFn('Scan inside archives')}"
          ></gz-checkbox>
        </gz-form-field>
        <gz-text id="scan-archives-text" fontSize="${FontSizes.PARAGRAPH_12}" class="helper-text mb-1"> </gz-text>

        <gz-form-field id="archive-limit-size" label="${translateFn('Limit archive size to (MB)')}">
          <gz-input-number
            gz-form-input
            min="1"
            value="10"
            inputWidth="${InputWidthValues.MEDIUM}"
            unitSuffix="${translateFn('MB')}"
          ></gz-input-number>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-form-field
          id="archive-max-depth"
          class="archive-max-depth"
          label="${translateFn('Maximum archive depth (levels)')}"
        >
          <gz-dropdown
            gz-form-input
            options="${JSON.stringify(MalwareScanHelper.getMaxDepthOptions())}"
            .value="${MalwareScanHelper.getMaxArchiveDepthFromType(this.type)}"
          ></gz-dropdown>
        </gz-form-field>

        <gz-form-field id="scan-email-archives" class="field-container">
          <gz-checkbox
            gz-form-input
            .checked="${MalwareScanHelper.getScanEmailArchivesFromType(this.type)}"
            id="scan-email-archives"
            class="mt-2"
            label="${translateFn('Scan email archives')}"
          ></gz-checkbox>
        </gz-form-field>
        <gz-text id="scan-emails-text" fontSize="${FontSizes.PARAGRAPH_12}" class="helper-text mt-2"> </gz-text>
        <div></div>
      </div>
    `;
  }

  private renderMiscellaneous(): TemplateResult {
    return html`
      <div class="miscellaneous mb-3 pl-2">
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="text-uppercase field-container form-subtitle mt-2"
          >${translateFn('Miscellaneous')}</gz-title
        >
        <gz-form-field
          class="${this.type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK
            ? 'hidden'
            : ''} field-container"
          id="scan-boot-sectors"
        >
          <gz-checkbox
            .checked="${this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.BOOT_SECTORS)}"
            gz-form-input
            id="scan-boot-sectors"
            label="${translateFn('Scan boot sectors')}"
          ></gz-checkbox>
        </gz-form-field>
        <gz-form-field
          class="${this.type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK ||
          this.type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_MEMORY
            ? 'hidden'
            : ''} field-container"
          id="scan-uefi"
        >
          <gz-checkbox
            .checked="${this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.UEFI)}"
            gz-form-input
            label="${translateFn('Scan UEFI')}"
          ></gz-checkbox>
        </gz-form-field>
        <gz-form-field
          class="${this.type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK
            ? 'hidden'
            : ''} field-container"
          id="scan-registry"
        >
          <gz-checkbox
            .checked="${this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.REGISTRY)}"
            gz-form-input
            id="scan-registry"
            label="${translateFn('Scan registry')}"
          ></gz-checkbox>
        </gz-form-field>
        <gz-form-field
          class="${this.type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK
            ? 'hidden'
            : ''} field-container"
          id="scan-rootkits"
        >
          <gz-checkbox
            @change="${this.handleRootkitsCheckboxChange}"
            .checked="${this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.ROOTKITS)}"
            gz-form-input
            id="scan-rootkits"
            label="${translateFn('Scan for rootkits')}"
          ></gz-checkbox>
        </gz-form-field>
        <gz-form-field id="scan-keylogger" class="field-container">
          <gz-checkbox
            .checked="${this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.KEYLOGGERS)}"
            gz-form-input
            id="scan-keylogger"
            label="${translateFn('Scan for keyloggers')}"
          ></gz-checkbox>
        </gz-form-field>
        <gz-form-field id="scan-network-shares" class="field-container">
          <gz-checkbox
            .checked="${this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.NETWORK_SHARES)}"
            gz-form-input
            id="scan-network-shares"
            label="${translateFn('Scan network shares')}"
          ></gz-checkbox>
        </gz-form-field>
        <gz-form-field
          class="${this.type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK
            ? 'hidden'
            : ''} field-container"
          id="scan-memory"
        >
          <gz-checkbox
            .checked="${this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.MEMORY)}"
            gz-form-input
            id="scan-memory"
            label="${translateFn('Scan memory')}"
          ></gz-checkbox>
        </gz-form-field>
        <gz-form-field
          class="${this.type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK
            ? 'hidden'
            : ''} field-container"
          id="scan-cookies"
        >
          <gz-checkbox
            .checked="${this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.COOKIES)}"
            gz-form-input
            id="scan-cookies"
            label="${translateFn('Scan cookies')}"
          ></gz-checkbox>
        </gz-form-field>
        <gz-form-field id="scan-new-and-changed-files" class="field-container">
          <gz-checkbox
            .checked="${this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.NEW_AND_CHANGED_FILES)}"
            gz-form-input
            id="scan-new-and-changed-files"
            label="${translateFn('Scan only new and changed files')}"
          ></gz-checkbox>
        </gz-form-field>
        <gz-form-field id="scan-pua" class="field-container">
          <gz-checkbox
            @change="${this.handlePuaCheckboxChange.bind(this)}"
            .checked="${this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.PUA)}"
            gz-form-input
            id="scan-pua"
            label="${translateFn('Scan for potentially unwanted applications (PUA)')}"
          ></gz-checkbox>
        </gz-form-field>
        <gz-form-field id="scan-after-product-update" class="field-container">
          <gz-checkbox
            .checked="${this.checkedMiscellaneous.includes(
              MALWARE_SCAN_MISCELLANEOUS.RESUME_SCAN_AFTER_PRODUCT_UPDATE
            )}"
            gz-form-input
            id="scan-after-product-update"
            label="${translateFn('Resume scan after product update')}"
          ></gz-checkbox>
        </gz-form-field>
        <gz-form-field
          class="${this.type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK
            ? 'hidden'
            : ''} field-container"
          id="scan-volumes"
        >
          <gz-checkbox
            .checked="${this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.DETACHABLE_VOLUMES)}"
            gz-form-input
            id="scan-volumes"
            label="${translateFn('Scan detachable volumes')}"
          ></gz-checkbox>
        </gz-form-field>
        <gz-form-field
          id="preserve-last-access"
          class="${this.type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_MEMORY ? 'hidden' : ''} field-container"
        >
          <gz-checkbox
            .checked="${this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.PRESERVE_LAST_ACCESS_TIME)}"
            gz-form-input
            id="preserve-last-access"
            label="${translateFn('Preserve last access time')}"
          ></gz-checkbox>
        </gz-form-field>
      </div>
    `;
  }

  private renderActions(): TemplateResult {
    return html`
      <div class="actions pl-2">
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="text-uppercase field-container form-subtitle"
          >${translateFn('Actions')}</gz-title
        >
        <gz-text class="mb-2" fontSize="${FontSizes.HEADING_6}">
          ${translateFn(
            'You can configure automatic response actions for detected threats, based on the detection verdict.'
          )}
        </gz-text>
        <div id="infected-files-action" class="mb-3">
          <gz-title
            fontSize="${FontSizes.PARAGRAPH_13}"
            fontWeight="${FontWeights.MEDIUM}"
            class="field-container form-subtitle actions-subtitle"
            >${translateFn('Infected objects')}</gz-title
          >
          <gz-form-field id="default-infected-action" label="${translateFn('Default action')}">
            <gz-dropdown
              gz-form-input
              options="${JSON.stringify(MalwareScanHelper.getInfectedFileActionOptions())}"
              .value="${MalwareScanHelper.getActionForInfectedFilesFromType(this.type)}"
              placeholder="${translateFn('Select default action')}"
            >
            </gz-dropdown>
          </gz-form-field>
        </div>
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_13}"
          fontWeight="${FontWeights.MEDIUM}"
          class="field-container form-subtitle actions-subtitle"
          >${translateFn('Rootkits')}</gz-title
        >
        <gz-form-field id="rootkits-action" label="${translateFn('Default action')}">
          <gz-dropdown
            gz-form-input
            options="${JSON.stringify(MalwareScanHelper.getRootkitsActionOptions())}"
            .value="${MalwareScanHelper.getRootkitActionFromTypeAndScanOption(
              this.type,
              this.scanOptionFormField?.value as number
            )}"
            placeholder="${translateFn('Select default action')}"
          >
          <gz-form-field id="rootkits-action" label="${translateFn('Default action')}">
            <gz-dropdown
              gz-form-input
              options="${JSON.stringify(MalwareScanHelper.getRootkitsActionOptions())}"
              .value="${MalwareScanHelper.getRootkitActionFromTypeAndScanOption(
                this.type,
                this.scanOptionFormField?.value as number
              )}"
              placeholder="${translateFn('Select default action')}"
            >
            </gz-dropdown>
          </gz-form-field>
        </div>

        <div id="pua-action" class="mb-3">
          <gz-text
            fontSize="${FontSizes.PARAGRAPH_13}"
            fontWeight="${FontWeights.MEDIUM}"
            class="field-container form-subtitle actions-subtitle"
          >${translateFn('PUA')}</gz-text
          >
          <gz-form-field id="default-pua-action" label="${translateFn('Default action')}">
            <gz-dropdown
              gz-form-input
              options="${JSON.stringify(MalwareScanHelper.getPuaActionOptions())}"
              .value="${MalwareScanHelper.getActionForInfectedFilesFromType(this.type)}"
              placeholder="${translateFn('Select default action')}"
            >
            </gz-dropdown>
          </gz-form-field>
        </div>
      </div>
    `;
  }

  private renderTargetSection(): TemplateResult {
    return html`
      <div class="inner-content mb-3">
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="text-uppercase field-container form-subtitle"
          >${MalwareScanHelper.addRequiredCharToStringWhenEditable(this.type, translateFn('Scan Target'))}</gz-title
        >
        <div id="target-header" class="target-header">
          <gz-form-field ?disabled="${this.isFormDisabled()}" id="scan-target-type" class="target-field mr-2">
            <gz-input
              gz-form-input
              inputWidth="${InputWidthValues.FULL}"
              id="scan-target-input"
              placeholder="${translateFn('Specify the path')}"
              label="${translateFn('Path')}"
              ?disabled="${this.isFormDisabled()}"
            ></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>
          <gz-icon-button
            id="add-target-icon-button"
            disabled
            @click="${this.handleAddScanTargetItem}"
            size="${ButtonSize.SIZE_32}"
            fontSize="22"
            buttonType="${ButtonType.SQUARE}"
            buttonFill="${ButtonFill.PRIMARY}"
            buttonColor="${ButtonColor.WHITE}"
            icon="ds-icon-add"
          ></gz-icon-button>
        </div>
        <gz-link
          class="link mb-2"
          href="${Application.Main.company.isPartner()
            ? 'https://www.bitdefender.com/business/support/en/77211-92656-system-variables.html'
            : 'https://www.bitdefender.com/business/support/en/77209-92656-system-variables.html'}"
        >
          ${translateFn('View list of suggestions')}
        </gz-link>
        ${this.renderScanTargetTable()}
      </div>
    `;
  }

  private renderScanTargetTable(): TemplateResult {
    return html`
      <gz-form-field id="form-scan-target-table">
        <gz-data-table
          id="scan-target-table"
          gz-form-input
          .disabled="${this.isFormDisabled()}"
          .columns="${MalwareScanHelper.getScanTargetColumns()}"
          .strings="${MalwareScanHelper.getStringsForTargetTable()}"
          .items="${this.scanFilesItems}"
          .extraValidator="${this.scanTargetExtraValidator}"
          allowrowdeletion
          allowrowediting
          @row-edited="${this.updateScanTargetValidators}"
          @row-deleted="${this.updateScanTargetValidators}"
          @empty-state-footer-button-clicked="${this.handleScanTargetEmptyGridClick}"
        ></gz-data-table>
      </gz-form-field>
    `;
  }

  private scanTargetExtraValidator(
    editedItem: ScanTargetFilesItem,
    oldItem: ScanTargetFilesItem,
    rowIndex: number,
    allItems: Array<ScanTargetFilesItem>
  ) {
    if (editedItem[ScanTargetColumn.PATH] === oldItem[ScanTargetColumn.PATH]) {
      return true;
    }

    return (
      allItems.find((item, index) => {
        if (index === rowIndex) {
          return;
        }

        if (item[ScanTargetColumn.PATH] === editedItem[ScanTargetColumn.PATH]) {
          return item;
        }
      }) === undefined
    );
  }

  private renderCustomExclusionTable(): TemplateResult {
    return html`
      <gz-data-table
        class="hidden"
        id="custom-exclusion-table"
        .columns="${MalwareScanHelper.getCustomExclusionsColumns()}"
        .strings="${MalwareScanHelper.getStringsForExclusionTable()}"
        .items="${this.exclusionItems}"
        .extraValidator="${this.customExclusionExtraValidator}"
        allowrowdeletion
        allowrowediting
        @row-deleted="${this.handleCustomExclusionDeleted}"
        @empty-state-footer-button-clicked="${this.handleCustomExclusionEmptyGridClick}"
      >
      </gz-data-table>
    `;
  }

  private customExclusionExtraValidator(
    editedItem: CustomExclusionItem,
    oldItem: CustomExclusionItem,
    rowIndex: number,
    allItems: Array<CustomExclusionItem>
  ) {
    if (isEqual(editedItem, oldItem)) {
      return true;
    }

    return (
      allItems.find((item, index) => {
        if (index === rowIndex) {
          return;
        }

        if (item[CustomExclusionColumn.TYPE] !== editedItem[CustomExclusionColumn.TYPE]) {
          return;
        }

        if (item[CustomExclusionColumn.PATH] === editedItem[CustomExclusionColumn.PATH]) {
          return item;
        }
      }) === undefined
    );
  }

  private handleCustomExclusionDeleted() {
    this.exclusionItems = this.customExclusionTable.getFormValue();
  }

  private renderNetworkCredentials(): TemplateResult {
    return html`
      <div
        class="${this.type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK
          ? ''
          : 'hidden'} credentials-form pb-3"
        id="network-credentials-form"
      >
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="text-uppercase field-container form-subtitle"
          >${translateFn('Define credentials for network file scanning')}</gz-title
        >
        <gz-form-field id="network-type-username" label="${translateFn('Username') + '*'}">
          <gz-input placeholder="Enter username" gz-form-input inputWidth="${InputWidthValues.LARGE}"></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>
        <gz-form-field id="network-type-password" label="${translateFn('Password') + '*'}">
          <gz-input
            placeholder="Enter password"
            gz-form-input
            inputWidth="${InputWidthValues.LARGE}"
            type="password"
          ></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>
    `;
  }

  private renderExclusions(): TemplateResult {
    return html`
      <div>
        <gz-title
          slot="dynamic-title-content"
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="accordion-title text-uppercase"
        >
          ${translateFn('Exclusions')}
        </gz-title>
        <div class="pt-2 inner-content">
          <gz-form-field ?disabled="${this.isFormDisabled()}" id="exclusions-menu">
            <gz-radio-group
              @radioSelectionChanged="${this.handleExclusionTypeChanged}"
              gz-form-input
              groupName="exclusions-menu"
              .selectedOption="${MalwareScanHelper.getDefaultExclusionFrom()}"
            >
              <div class="field-container radio-container">
                <gz-radio
                  name="exclusions-menu"
                  .value="${Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_PROFILE.USE_GLOBAL}"
                >
                </gz-radio>
                <gz-text id="exclusions-text" fontSize="14" class="hint-text radio-label"></gz-text>
              </div>

              <gz-radio
                class="field-container"
                name="exclusions-menu"
                .value="${Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_PROFILE.USE_CUSTOM}"
                label="${translateFn('Define custom exclusions for this scan:')}"
              ></gz-radio>
            </gz-radio-group>
          </gz-form-field>
          <div id="exclusion-form-input" class="exclusion-form-input hidden">
            <gz-form-field id="exclusion-type" class="mr-2">
              <gz-dropdown
                gz-form-input
                options="${JSON.stringify(MalwareScanHelper.getExclusionTypeSelectOptions())}"
                value="${Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_TYPE.FILE}"
                label="${translateFn('Type')}"
                placeholder="${translateFn('Select exclusion type')}"
                @selectionChanged="${this.handleCustomExclusionTypeChanged}"
              >
              </gz-dropdown>
            </gz-form-field>
            <gz-form-field id="exclusion-path" class="mr-2">
              <gz-input
                id="exclusion-path-input"
                gz-form-input
                inputWidth="${InputWidthValues.FULL}"
                label="${MalwareScanHelper.getLabelDependingOnCustomExclusionType(this.customExclusionType)}"
                placeholder="${MalwareScanHelper.getPlaceholderDependingOnCustomExclusionType(
                  this.customExclusionType
                )}"
              ></gz-input>
              <gz-input-error></gz-input-error>
              <gz-link class="link mt-1" href="${this.getKbLinkForCustomExclusions()}">
                ${translateFn('View list of suggestions')}
              </gz-link>
            </gz-form-field>
            <gz-icon-button
              id="add-custom-exclusion"
              disabled
              @click="${this.handleAddExclusion}"
              size="${ButtonSize.SIZE_32}"
              fontSize="22"
              buttonType="${ButtonType.SQUARE}"
              buttonFill="${ButtonFill.PRIMARY}"
              buttonColor="${ButtonColor.WHITE}"
              icon="ds-icon-add"
            ></gz-icon-button>
          </div>
          ${this.renderCustomExclusionTable()}
        </div>
      </div>
    `;
  }

  private handleExclusionTypeChanged(event: CustomEvent) {
    event.stopImmediatePropagation();
    const newSelection = event.detail;
    if (newSelection?.selectedValue === Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_PROFILE.USE_GLOBAL) {
      elementClassListAdd(this.customExclusionsContainer, 'hidden');
      elementClassListAdd(this.customExclusionTable, 'hidden');
    } else {
      elementClassListRemove(this.customExclusionsContainer, 'hidden');
      elementClassListRemove(this.customExclusionTable, 'hidden');
    }
  }

  private async handleTypeChanged(event: CustomEvent) {
    event.stopImmediatePropagation();
    if (!event.detail || event.detail.value === undefined || event.detail.value === '') {
      return;
    }

    this.propsChangedFromInside = true;

    const selection = event.detail as SelectOption;
    const newType = selection.value as number;

    this.type = newType;
    this.lowPriorityFormField.value = false;
    this.shutDownFormField.value = false;
    this.pauseWhenOnBatteryField.value = false;
    this.cpuEnableUsageField.value = false;
    this.maximumUsageField.value = MalwareScanHelper.getDefaultValueMaximumCpuUsageControl();

    // only set value if changed, otherwise an event will be fired
    const newScanOptionValue = MalwareScanHelper.getDefaultOptionFromType(this.type);
    if (this.scanOptionFormField.value !== newScanOptionValue) {
      this.scanOptionFormField.value = newScanOptionValue;
    }

    // only set value if changed, otherwise an event will be fired
    const newFileTypeValue = MalwareScanHelper.getDefaultFileType();
    if (this.fileTypeFormField.value !== newFileTypeValue) {
      this.fileTypeFormField.value = newFileTypeValue;
    }

    this.customExtensionsChipsGenerator.setFormValue([]);

    this.archivesFormGroup.value = {
      enable: MalwareScanHelper.getScanInsideArchiveFromType(this.type),
      scanEmail: MalwareScanHelper.getScanEmailArchivesFromType(this.type),
      settings: {
        limitArchiveSize: MalwareScanHelper.getLimitArchiveSizeFromType(this.type),
        maximumArchiveDepth: MalwareScanHelper.getMaxArchiveDepthFromType(this.type),
      },
    };

    this.checkedMiscellaneous = MalwareScanHelper.getMiscellaneousFromType(newType);
    this.setMiscellaneousFormValue();

    this.actionFormGroup.value = {
      whenInfected: {
        action: MalwareScanHelper.getActionForInfectedFilesFromType(this.type),
      },
      whenRootKitAction: {
        action: MalwareScanHelper.getRootkitActionFromTypeAndScanOption(this.type, this.scanOptionFormField?.value),
      },
      whenPua: {
        action: MalwareScanHelper.getDefaultActionForPua(),
      }
    };

    this.scanTargetTypeFormField.value = '';
    this.scanTargetTable.items = MalwareScanHelper.getDefaultScanTargetPathFromType(newType);
    this.scanTargetTable.disabled = this.isFormDisabled();

    if (this.isFormDisabled()) {
      if (!this.target.classList.contains('disabled')) {
        this.target.classList.add('disabled');
      }

      if (!this.scanTargetTypeFormField.classList.contains('disabled')) {
        this.scanTargetTypeFormField.classList.add('disabled');
      }

      if (!this.optionsForm.classList.contains('disabled')) {
        this.optionsForm.classList.add('disabled');
      }
    } else {
      this.target.classList.remove('disabled');
      this.scanTargetTypeFormField.classList.remove('disabled');
      this.optionsForm.classList.remove('disabled');
    }

    this.scanFilesItems = MalwareScanHelper.getDefaultScanTargetPathFromType(this.type);
    this.scanTargetTableFormField.validate();

    this.exclusionItems = [];
    this.exclusionGeneralTypeFormField.value = MalwareScanHelper.getDefaultExclusionFrom();
    this.exclusionTypeFormField.value = '';
    this.exclusionPathFormField.value = '';
    this.customExclusionType = null;

    this.disableFormIfNecessary();
    this.nameFormField.Input.setValue(MalwareScanHelper.getScanTitleFromType(this.type));
    await this.updateComplete;
    if (this.type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_NETWORK) {
      this.scanTargetTable.setFormValue([]);
      this.usernameFormField.validate();
      this.passwordFormField.validate();
      this.scanTargetTableFormField.validate();
      this.rootkitsActionFormField.disabled = true;
    }

    if (this.type === Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_CUSTOM) {
      this.scanTargetTable.setFormValue([]);
      this.scanTargetTableFormField.validate();
    }

    this.updateFormValidators();
    this.propsChangedFromInside = false;

    // pua actions disabled state
    this.defaultPuaActionFormField.disabled = this.formGroup.disabled || !this.scanPuaFormField?.value;
  }

  private async handleScanOptionChanged(event: CustomEvent) {
    event.stopImmediatePropagation();
    this.propsChangedFromInside = true;
    const newValue = event.detail.selectedValue as number;

    if (!newValue) {
      throw Error('Event does not contain new selected value.');
    }

    // nothing should happen when switching to custom settings
    if (newValue === Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_CUSTOM) {
      return;
    }

    this.propsChangedFromInside = true;

    if (this.scanOptionFormField.value !== newValue) {
      this.scanOptionFormField.value = newValue;
    }

    const newFileTypeValue = MalwareScanHelper.getFileTypeFromScanOption(newValue);
    if (this.fileTypeFormField.value !== newFileTypeValue) {
      this.fileTypeFormField.value = newFileTypeValue;
    }
    this.archivesFormGroup.value = MalwareScanHelper.getArchiveSettingsFromScanOption(newValue);
    this.rootkitsActionFormField.value = MalwareScanHelper.getRootkitActionFromTypeAndScanOption(this.type, newValue);

    this.checkedMiscellaneous = MalwareScanHelper.getMiscellaneousFromScanOption(newValue);
    this.setMiscellaneousFormValue();

    await this.updateComplete;
    this.propsChangedFromInside = false;
  }

  private handleAddExclusion() {
    this.customExclusionTable.addItem({
      [CustomExclusionColumn.TYPE]: this.exclusionTypeFormField.value as string,
      [CustomExclusionColumn.PATH]: (this.exclusionPathFormField.value as string).trim(),
    });
    this.exclusionItems = this.customExclusionTable.getFormValue();

    this.exclusionPathFormField.value = '';
    this.exclusionPathInput.inputFocus();
  }

  private handleAddScanTargetItem() {
    this.scanTargetTable.addItem({ [ScanTargetColumn.PATH]: this.scanTargetTypeFormField.value.toString().trim() });
    this.scanTargetTypeFormField.value = '';
    this.updateScanTargetValidators();
    this.scanTargetInput.inputFocus();
    this.scanTargetTableFormField.validate();
  }

  private disableFormIfNecessary() {
    this.formGroup.setDisabled(this.isFormDisabled());
  }

  private isFormDisabled(): boolean {
    const disabledTypes = [
      Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_QUICK,
      Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_FULL,
      Constants.Tasks.Endpoints.SCAN_TYPES.SCAN_TYPE_MEMORY,
    ];
    return disabledTypes.includes(this.type);
  }

  private setMiscellaneousFormValue() {
    this.miscellaneousFormGroup.value = {
      scanBoot:
        !isHidden(this.scanBootFormField) &&
        this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.BOOT_SECTORS),
      scanUefi:
        !isHidden(this.scanUefiFormField) && this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.UEFI),
      scanRegistry:
        !isHidden(this.scanRegistryFormField) &&
        this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.REGISTRY),
      scanRootkits:
        !isHidden(this.scanRootKitsFormField) &&
        this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.ROOTKITS),
      ignoreKeyloggers:
        !isHidden(this.scanKeyloggerFormField) &&
        this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.KEYLOGGERS),
      scanNetworkFiles:
        !isHidden(this.scanNetworkSharesFormField) &&
        this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.NETWORK_SHARES),
      scanMemory:
        !isHidden(this.scanMemoryFormField) && this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.MEMORY),
      scanCookie:
        !isHidden(this.scanCookiesFormField) && this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.COOKIES),
      scanNewChanged:
        !isHidden(this.scanNewAndChangedFilesFormField) &&
        this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.NEW_AND_CHANGED_FILES),
      scanPUA: !isHidden(this.scanPuaFormField) && this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.PUA),
      resumeIfInterrupted:
        !isHidden(this.scanAfterProductUpdateFormField) &&
        this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.RESUME_SCAN_AFTER_PRODUCT_UPDATE),
      scanDetachableVolumes:
        !isHidden(this.scanVolumesFormField) &&
        this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.DETACHABLE_VOLUMES),
      preserveLastAccessTime:
        !isHidden(this.preserveLastAccessFormField) &&
        this.checkedMiscellaneous.includes(MALWARE_SCAN_MISCELLANEOUS.PRESERVE_LAST_ACCESS_TIME),
    };
  }

  private handleShutDownToggled(event: CustomEvent) {
    this.shouldShowWarningInfo = event.detail.checked;
  }

  private handleEnableCpuUsageToggled(event: CustomEvent) {
    const actualValue = event.detail.checked;
    this.shouldShowMaximumUsageDropdown = actualValue;

    if (!actualValue) {
      this.maximumUsageField.Input.setFormValue(MalwareScanHelper.getDefaultValueMaximumCpuUsageControl());
    }
  }

  private handleCustomExclusionTypeChanged(event: CustomEvent) {
    if (!event.detail.value) {
      return;
    }

    this.customExclusionType = event.detail.value;
    this.exclusionPathFormField.validate();
  }

  private handleScanInsideArchiveToggle(event: CustomEvent) {
    const actualValue = event.detail.checked;
    if (actualValue === true) {
      this.archiveScanLimitFormField.disabled = false;
      this.archiveMaxDepthFormField.disabled = false;
    } else {
      this.archiveScanLimitFormField.disabled = true;
      this.archiveMaxDepthFormField.disabled = true;
    }

    // should trigger scanOption changed
    if (
      actualValue === false &&
      this.scanOptionFormField.value !== Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_PERMISSIVE
    ) {
      this.scanOptionFormField.Input.selectOption(Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_CUSTOM);
    }

    if (
      actualValue === true &&
      this.scanOptionFormField.value === Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_PERMISSIVE
    ) {
      this.scanOptionFormField.Input.selectOption(Constants.Tasks.Endpoints.SCAN_PROFILES.SCAN_PROFILE_CUSTOM);
    }
  }

  private handleRootkitsCheckboxChange(event: CustomEvent) {
    const actualValue = event.detail.checked;

    this.rootkitsActionFormField.disabled = actualValue !== true;
  }

  private handlePuaCheckboxChange(event: CustomEvent) {
    const actualValue = event.detail.checked;

    this.defaultPuaActionFormField.disabled = actualValue !== true;
  }

  private handleScanTargetEmptyGridClick() {
    this.scanTargetInput.inputFocus();
  }

  private handleCustomExclusionEmptyGridClick() {
    this.exclusionTypeFormField.Input.toggleDropdown();
  }

  private handleFileTypeSelectionChanged(event: CustomEvent) {
    const newValue = event.detail.value as number;

    if (newValue === Constants.Tasks.Endpoints.SCAN_OPTIONS.FILE_TYPES.CUSTOM_EXTENTIONS) {
      elementClassListRemove(this.customDefinedExtensionsContainer, 'hidden');
    } else {
      elementClassListAdd(this.customDefinedExtensionsContainer, 'hidden');
      this.customExtensionsChipsGenerator.setFormValue([]);
    }
  }

  private getKbLinkForCustomExclusions(): string {
    if (this.customExclusionType === Constants.Tasks.Endpoints.SCAN_EXCLUSIONS_TYPE.EXTENSION) {
      return Application.Main.company.isPartner()
        ? 'https://www.bitdefender.com/business/support/en/77211-95338-supported-file-types.html'
        : 'https://www.bitdefender.com/business/support/en/77209-95338-supported-file-types.html';
    }

    return Application.Main.company.isPartner()
      ? 'https://www.bitdefender.com/business/support/en/77211-92656-system-variables.html'
      : 'https://www.bitdefender.com/business/support/en/77209-92656-system-variables.html';
  }
}
